import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Edit, History, Save, RefreshCw, Eye, RotateCcw } from 'lucide-react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface SchemaVersion {
  id: string;
  schema_id: string;
  connection_type: string;
  table_name: string;
  schema_content: string;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  changed_by_email: string;
  change_reason?: string;
}

const SchemaManagementEditor: React.FC = () => {
  const { toast } = useToast();
  const [schemas, setSchemas] = useState<SchemaVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConnectionType, setSelectedConnectionType] = useState<string>('all');
  const [editingSchema, setEditingSchema] = useState<SchemaVersion | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState<string | null>(null);
  const [versionHistory, setVersionHistory] = useState<SchemaVersion[]>([]);


  const connectionTypes = ['all', 'shopify', 'amazon'];

  useEffect(() => {
    fetchSchemas();
  }, []);

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('schema_management_versions')
        .select('*')
        .eq('is_active', true)
        .order('connection_type', { ascending: true })
        .order('table_name', { ascending: true });

      if (error) throw error;
      setSchemas(data || []);
    } catch (error) {
      console.error('Error fetching schemas:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch schemas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionHistory = async (schemaId: string) => {
    try {
      const { data, error } = await supabase
        .from('schema_management_versions')
        .select('*')
        .eq('schema_id', schemaId)
        .order('version', { ascending: false });

      if (error) throw error;
      setVersionHistory(data || []);
    } catch (error) {
      console.error('Error fetching version history:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch version history',
        variant: 'destructive',
      });
    }
  };

  const saveSchema = async (updatedContent: string, changeReason: string) => {
    if (!editingSchema) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the current highest version for this schema
      const { data: existingVersions, error: versionError } = await supabase
        .from('schema_management_versions')
        .select('version')
        .eq('schema_id', editingSchema.schema_id)
        .order('version', { ascending: false })
        .limit(1);

      if (versionError) throw versionError;

      const nextVersion = (existingVersions?.[0]?.version || 0) + 1;

      // Deactivate current active version
      await supabase
        .from('schema_management_versions')
        .update({ is_active: false })
        .eq('schema_id', editingSchema.schema_id)
        .eq('is_active', true);

      // Insert new version
      const { error: insertError } = await supabase
        .from('schema_management_versions')
        .insert({
          schema_id: editingSchema.schema_id,
          connection_type: editingSchema.connection_type,
          table_name: editingSchema.table_name,
          schema_content: updatedContent,
          version: nextVersion,
          is_active: true,
          changed_by_user_id: user.id,
          changed_by_email: user.email || '',
          change_reason: changeReason,
        });

      if (insertError) throw insertError;

      toast({
        title: 'Success',
        description: `Schema version ${nextVersion} saved successfully`,
      });

      // Automatically regenerate embeddings in the background
      toast({
        title: 'Processing',
        description: 'Regenerating embeddings for updated schema...',
      });

      // Use background task to regenerate embeddings without blocking UI
      invokeFunction(supabase, 'generate-schema-embeddings')
        .then(({ error }) => {
          if (error) {
            console.error('Error regenerating embeddings:', error);
            toast({
              title: 'Warning',
              description: 'Schema saved but embeddings regeneration failed',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Complete',
              description: 'Schema and embeddings updated successfully',
            });
          }
        })
        .catch((error) => {
          console.error('Error regenerating embeddings:', error);
          toast({
            title: 'Warning',
            description: 'Schema saved but embeddings regeneration failed',
            variant: 'destructive',
          });
        });

      setEditingSchema(null);
      fetchSchemas();
    } catch (error) {
      console.error('Error saving schema:', error);
      toast({
        title: 'Error',
        description: 'Failed to save schema',
        variant: 'destructive',
      });
    }
  };

  const revertToVersion = async (version: SchemaVersion) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get the current highest version for this schema
      const { data: existingVersions, error: versionError } = await supabase
        .from('schema_management_versions')
        .select('version')
        .eq('schema_id', version.schema_id)
        .order('version', { ascending: false })
        .limit(1);

      if (versionError) throw versionError;

      const nextVersion = (existingVersions?.[0]?.version || 0) + 1;

      // Deactivate current active version
      await supabase
        .from('schema_management_versions')
        .update({ is_active: false })
        .eq('schema_id', version.schema_id)
        .eq('is_active', true);

      // Insert new version with reverted content
      const { error: insertError } = await supabase
        .from('schema_management_versions')
        .insert({
          schema_id: version.schema_id,
          connection_type: version.connection_type,
          table_name: version.table_name,
          schema_content: version.schema_content,
          version: nextVersion,
          is_active: true,
          changed_by_user_id: user.id,
          changed_by_email: user.email || '',
          change_reason: `Reverted to version ${version.version}`,
        });

      if (insertError) throw insertError;

      toast({
        title: 'Success',
        description: `Successfully reverted to version ${version.version}`,
      });

      // Automatically regenerate embeddings in the background
      toast({
        title: 'Processing',
        description: 'Regenerating embeddings for restored schema...',
      });

      invokeFunction(supabase, 'generate-schema-embeddings')
        .then(({ error }) => {
          if (error) {
            console.error('Error regenerating embeddings:', error);
            toast({
              title: 'Warning',
              description: 'Schema restored but embeddings regeneration failed',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Complete',
              description: 'Schema and embeddings updated successfully',
            });
          }
        })
        .catch((error) => {
          console.error('Error regenerating embeddings:', error);
          toast({
            title: 'Warning',
            description: 'Schema restored but embeddings regeneration failed',
            variant: 'destructive',
          });
        });

      setShowVersionHistory(null);
      fetchSchemas();
      fetchVersionHistory(version.schema_id);
    } catch (error) {
      console.error('Error reverting schema:', error);
      toast({
        title: 'Error',
        description: 'Failed to revert to selected version',
        variant: 'destructive',
      });
    }
  };


  const filteredSchemas = schemas.filter(schema => {
    const matchesSearch = schema.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      schema.schema_content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedConnectionType === 'all' || schema.connection_type === selectedConnectionType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        <span>Loading schemas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Schema Management</h2>
        <p className="text-muted-foreground">Manage database schema descriptions for AI assistance. Embeddings are automatically regenerated when schemas are saved.</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search schemas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {connectionTypes.map((type) => (
                <Button
                  key={type}
                  variant={selectedConnectionType === type ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedConnectionType(type)}
                  className="capitalize"
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Table Name</TableHead>
                <TableHead>Connection Type</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSchemas.map((schema) => (
                <TableRow key={schema.id}>
                  <TableCell className="font-medium">{schema.table_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {schema.connection_type}
                    </Badge>
                  </TableCell>
                  <TableCell>v{schema.version}</TableCell>
                  <TableCell>{format(new Date(schema.updated_at), 'MMM d, yyyy')}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowVersionHistory(schema.schema_id);
                          fetchVersionHistory(schema.schema_id);
                        }}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingSchema(schema)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredSchemas.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || selectedConnectionType !== 'all'
                ? 'No schemas match your search criteria'
                : 'No schemas found'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Schema Dialog */}
      {editingSchema && (
        <SchemaEditDialog
          schema={editingSchema}
          onSave={saveSchema}
          onClose={() => setEditingSchema(null)}
        />
      )}

      {/* Version History Dialog */}
      {showVersionHistory && (
        <VersionHistoryDialog
          schemaId={showVersionHistory}
          versions={versionHistory}
          onClose={() => setShowVersionHistory(null)}
          onRevert={revertToVersion}
        />
      )}
    </div>
  );
};

interface SchemaEditDialogProps {
  schema: SchemaVersion;
  onSave: (content: string, reason: string) => void;
  onClose: () => void;
}

const SchemaEditDialog: React.FC<SchemaEditDialogProps> = ({ schema, onSave, onClose }) => {
  const [content, setContent] = useState(schema.schema_content);
  const [changeReason, setChangeReason] = useState('');

  const handleSave = () => {
    if (!changeReason.trim()) {
      return;
    }
    onSave(content, changeReason);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            Edit Schema: {schema.table_name} ({schema.connection_type})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto">
          <div>
            <label className="text-sm font-medium">Change Reason</label>
            <Input
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Describe what you're changing..."
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium">Schema Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-96 font-mono text-sm"
              placeholder="Enter schema content..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!changeReason.trim()}>
            <Save className="h-4 w-4 mr-2" />
            Save Version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface VersionHistoryDialogProps {
  schemaId: string;
  versions: SchemaVersion[];
  onClose: () => void;
  onRevert: (version: SchemaVersion) => void;
}

const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  schemaId,
  versions,
  onClose,
  onRevert
}) => {
  const [selectedVersion, setSelectedVersion] = useState<SchemaVersion | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  const handleViewSchema = (version: SchemaVersion) => {
    setSelectedVersion(version);
    setViewDialogOpen(true);
  };

  const handleRevertToVersion = async (version: SchemaVersion) => {
    setReverting(true);
    try {
      await onRevert(version);
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>

          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Changed By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell>v{version.version}</TableCell>
                    <TableCell>{version.changed_by_email}</TableCell>
                    <TableCell>{format(new Date(version.created_at), 'MMM d, yyyy HH:mm')}</TableCell>
                    <TableCell>{version.change_reason || 'No reason provided'}</TableCell>
                    <TableCell>
                      {version.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewSchema(version)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                        {!version.is_active && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                disabled={reverting}
                              >
                                <RotateCcw className="h-3 w-3" />
                                Revert
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Revert to Version {version.version}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will create a new version with the content from version {version.version}.
                                  The current schema will be replaced immediately and embeddings will be regenerated.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRevertToVersion(version)}
                                  disabled={reverting}
                                >
                                  {reverting ? 'Reverting...' : 'Revert'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Schema Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Schema Content - Version {selectedVersion?.version}
              {selectedVersion?.is_active && <Badge className="ml-2">Active</Badge>}
            </DialogTitle>
            <DialogDescription>
              {selectedVersion?.table_name} ({selectedVersion?.connection_type}) - Changed by {selectedVersion?.changed_by_email} on{' '}
              {selectedVersion && format(new Date(selectedVersion.created_at), 'MMM d, yyyy HH:mm')}
              {selectedVersion?.change_reason && (
                <span className="block mt-1">
                  <strong>Reason:</strong> {selectedVersion.change_reason}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={selectedVersion?.schema_content || ''}
              readOnly
              className="min-h-[400px] font-mono text-sm"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SchemaManagementEditor;